import { cn } from "@/lib/utils";

export interface ProgressBarV2Props {
  value: number;
  min?: number;
  max?: number;
  label?: string;
  showValue?: boolean;
  variant?: "default" | "success" | "warning" | "danger";
  className?: string;
}

const BAR_COLORS: Record<NonNullable<ProgressBarV2Props["variant"]>, string> = {
  default: "bg-[var(--v2-accent)]",
  success: "bg-[var(--v2-success,#067647)]",
  warning: "bg-amber-500",
  danger: "bg-[var(--v2-danger,#b42318)]",
};

export function ProgressBarV2({
  value,
  min = 0,
  max = 100,
  label,
  showValue = true,
  variant = "default",
  className,
}: ProgressBarV2Props) {
  const clamped = Math.max(min, Math.min(value, max));
  const pct =
    max === min ? 0 : Math.round(((clamped - min) / (max - min)) * 100);
  const labelId = label
    ? `progress-${label.replace(/\s+/g, "-").toLowerCase()}`
    : undefined;

  return (
    <div className={cn("space-y-1.5", className)}>
      {(label || showValue) && (
        <div className="flex items-center justify-between text-xs">
          {label && (
            <span
              id={labelId}
              className="font-semibold text-[var(--v2-text-strong)]"
            >
              {label}
            </span>
          )}
          {showValue && (
            <span className="text-[var(--v2-text-muted)]">%{pct}</span>
          )}
        </div>
      )}

      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-labelledby={labelId}
        className="h-2 w-full overflow-hidden rounded-full bg-slate-100"
      >
        <div
          className={cn(
            "h-full rounded-full transition-all duration-300 ease-out",
            BAR_COLORS[variant]
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
